import { z } from 'zod';
import { PromotionCreditType } from '../../constants/promotion-credit-type.js';
import { optionalUuidString, requiredEnumIdString } from '../zod-helpers/index.js';
import { PROMOTION_CREDIT_CONSTRAINTS } from './constraints.js';
import { creditAmountInput } from './credit-amount-input.js';
import { dateStringInput } from './date-string-input.js';
import { promotionCreditLineUpdateFormSchema } from './line-update-form.js';
import { promotionCreditLinesMessage } from './lines-message.js';
import { lowercaseUuid } from './lowercase-uuid.js';
import { numberStringInput } from './number-string-input.js';
import { OVERPAYMENT_NOT_CHANGEABLE_MESSAGE } from './overpayment-not-changeable-message.js';
import { OVERPAYMENT_TYPE_LOCKED_MESSAGE } from './overpayment-type-locked-message.js';
import { refineCreditForm } from './refine-credit-form.js';
const { details, notes, lines, amount } = PROMOTION_CREDIT_CONSTRAINTS;
const OVERPAYMENT_ID = String(PromotionCreditType.OVERPAYMENT);
const lengthMessage = (label, maxLength) => `${label} must be ${maxLength} characters or less.`;
// A stored credit always has both dates and the API cannot clear one, so the edit form requires them.
const requiredDateStringInput = (label) => dateStringInput(label).refine((value) => value !== '', { message: `${label} is required.` });
// The API blocks only a change to or from Overpayment, so an Overpayment credit may keep its own type.
const creditTypeInput = (storedTypeId) => {
    const typeInput = requiredEnumIdString(PromotionCreditType, 'Credit type is required.', 'Invalid credit type selection.');
    if (storedTypeId === PromotionCreditType.OVERPAYMENT) {
        return typeInput.refine((value) => value === OVERPAYMENT_ID, {
            message: OVERPAYMENT_TYPE_LOCKED_MESSAGE,
        });
    }
    return typeInput.refine((value) => value !== OVERPAYMENT_ID, {
        message: OVERPAYMENT_NOT_CHANGEABLE_MESSAGE,
    });
};
/**
 * The edit form's schema; pass the credit's stored type so an Overpayment credit can be edited without changing its type.
 * Pass `checkLinesSum: false` while the amount and lines are locked, so a stored mismatch between them does not block an unrelated edit.
 */
export const buildUpdatePromotionCreditFormSchema = (storedTypeId, options = {}) => z
    .object({
    promotionCreditTypeId: creditTypeInput(storedTypeId),
    amount: numberStringInput(creditAmountInput, amount.label),
    dateApplicable: requiredDateStringInput('Issued date'),
    expirationDate: requiredDateStringInput('Expiration date'),
    details: z
        .string()
        .trim()
        .max(details.maxLength, lengthMessage(details.label, details.maxLength)),
    notes: z.string().trim().max(notes.maxLength, lengthMessage(notes.label, notes.maxLength)),
    promotionId: optionalUuidString('Invalid promotion selection.').transform(lowercaseUuid),
    lines: z
        .array(promotionCreditLineUpdateFormSchema)
        .max(lines.max, promotionCreditLinesMessage),
})
    .superRefine((value, context) => refineCreditForm(value, context, options));
//# sourceMappingURL=build-update-form-schema.js.map